import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { addBreadcrumb, setTag } from '@/lib/sentry';

/**
 * Hook to track page navigation for Sentry breadcrumbs
 */
export function useNavigationTracking() {
  const [location] = useLocation();

  useEffect(() => {
    addBreadcrumb('navigation', `Navigated to ${location}`, { url: location }, 'info');
    setTag('page', location);
  }, [location]);
}

/**
 * Hook to track user interactions
 */
export function useInteractionTracking(componentName: string) {
  const trackClick = (elementName: string, data?: Record<string, unknown>) => {
    addBreadcrumb('ui.click', `${componentName}: ${elementName}`, data, 'info');
  };

  const trackChange = (fieldName: string, value?: string) => {
    addBreadcrumb('ui.input', `${componentName}: ${fieldName} changed`, { hasValue: !!value }, 'info');
  };

  const trackSubmit = (formName: string, data?: Record<string, unknown>) => {
    addBreadcrumb('form.submit', `${componentName}: ${formName} submitted`, data, 'info');
  };

  return { trackClick, trackChange, trackSubmit };
}

/**
 * Track checkout flow progress
 */
export function useCheckoutTracking() {
  const trackStep = (stepName: string, stepNumber: number) => {
    addBreadcrumb('checkout', `Step ${stepNumber}: ${stepName}`, { stepNumber, stepName }, 'info');
    setTag('checkout.step', stepName);
  };

  const trackBiomeSelection = (biome: string) => {
    addBreadcrumb('checkout', `Selected biome: ${biome}`, { biome }, 'info');
  };

  const trackDeliverySelection = (week: string) => {
    addBreadcrumb('checkout', `Selected delivery week: ${week}`, { week }, 'info');
  };

  const trackPaymentInitiated = (amount: number) => {
    addBreadcrumb('checkout', 'Payment initiated', { amount }, 'info');
  };

  const trackPaymentSuccess = (orderId: string) => {
    addBreadcrumb('checkout', 'Payment successful', { orderId }, 'info');
  };

  const trackPaymentError = (error: string) => {
    addBreadcrumb('checkout', 'Payment error', { error }, 'error');
  };

  return {
    trackStep,
    trackBiomeSelection,
    trackDeliverySelection,
    trackPaymentInitiated,
    trackPaymentSuccess,
    trackPaymentError,
  };
}
